import { useState, useEffect, useMemo } from "react"
import { authFetch } from "../../utils/api"

const API = process.env.REACT_APP_API_URL
const POR_PAGINA = 10

const FORM_VACIO = { marcaNombre: "", codigo: "", descripcion: "", unidad: "" }

function GestionProductos() {
  const [productos, setProductos]   = useState([])
  const [marcas, setMarcas]         = useState([])
  const [form, setForm]             = useState(FORM_VACIO)
  const [editandoId, setEditandoId] = useState(null)
  const [editForm, setEditForm]     = useState(null)
  const [mensaje, setMensaje]       = useState(null)
  const [nuevaMarca, setNuevaMarca]         = useState("")
  const [mensajeMarca, setMensajeMarca]     = useState(null)
  const [editandoMarcaId, setEditandoMarcaId]     = useState(null)
  const [editandoMarcaNombre, setEditandoMarcaNombre] = useState("")
  const [busqueda, setBusqueda]     = useState("")
  const [pagina, setPagina]         = useState(1)

  const cargarProductos = () =>
    authFetch(`${API}/productos`).then(r => r.json()).then(setProductos)

  const cargarMarcas = () =>
    authFetch(`${API}/marcas`).then(r => r.json()).then(setMarcas)

  useEffect(() => { cargarProductos(); cargarMarcas() }, [])

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
  const handleEditChange = (e) => setEditForm(prev => ({ ...prev, [e.target.name]: e.target.value }))

  const handleBusqueda = (e) => {
    setBusqueda(e.target.value)
    setPagina(1)
    setEditandoId(null)
    setEditForm(null)
  }

  const productosFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    if (!q) return productos
    return productos.filter(p =>
      p.codigo.toLowerCase().includes(q) ||
      p.descripcion.toLowerCase().includes(q) ||
      p.marca.toLowerCase().includes(q)
    )
  }, [productos, busqueda])

  const totalPaginas = Math.max(1, Math.ceil(productosFiltrados.length / POR_PAGINA))
  const paginaReal   = Math.min(pagina, totalPaginas)
  const productosPagina = productosFiltrados.slice((paginaReal - 1) * POR_PAGINA, paginaReal * POR_PAGINA)

  const handleCrear = async () => {
    setMensaje(null)
    if (!form.marcaNombre || !form.codigo || !form.descripcion || !form.unidad) {
      setMensaje({ tipo: "error", texto: "Completá todos los campos" })
      return
    }
    try {
      const res = await authFetch(`${API}/productos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, marca: form.marcaNombre }),
      })
      if (!res.ok) {
        const err = await res.json()
        setMensaje({ tipo: "error", texto: err.error || "Error al crear" })
        return
      }
      setMensaje({ tipo: "ok", texto: "Producto creado correctamente" })
      setForm(FORM_VACIO)
      cargarProductos()
    } catch {
      setMensaje({ tipo: "error", texto: "Error de conexión" })
    }
  }

  const handleEditar = (p) => {
    if (editandoId === p.id) { setEditandoId(null); setEditForm(null); return }
    setEditandoId(p.id)
    setEditForm({ marcaNombre: p.marca, codigo: p.codigo, descripcion: p.descripcion, unidad: p.unidad })
    setMensaje(null)
  }

  const handleGuardar = async () => {
    setMensaje(null)
    try {
      const res = await authFetch(`${API}/productos/${editandoId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...editForm, marca: editForm.marcaNombre }),
      })
      if (!res.ok) {
        const err = await res.json()
        setMensaje({ tipo: "error", texto: err.error || "Error al guardar" })
        return
      }
      setMensaje({ tipo: "ok", texto: "Producto actualizado" })
      setEditandoId(null); setEditForm(null)
      cargarProductos()
    } catch {
      setMensaje({ tipo: "error", texto: "Error de conexión" })
    }
  }

  const handleBaja = async (id) => {
    if (!window.confirm("¿Dar de baja este producto?")) return
    await authFetch(`${API}/productos/${id}`, { method: "DELETE" })
    cargarProductos()
  }

  const handleAgregarMarca = async () => {
    setMensajeMarca(null)
    if (!nuevaMarca.trim()) return
    try {
      const res = await authFetch(`${API}/marcas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nombre: nuevaMarca.trim() }),
      })
      if (!res.ok) {
        const err = await res.json()
        setMensajeMarca({ tipo: "error", texto: err.error || "Error al agregar" })
        return
      }
      setNuevaMarca("")
      cargarMarcas()
    } catch {
      setMensajeMarca({ tipo: "error", texto: "Error de conexión" })
    }
  }

  const handleEditarMarca = (m) => {
    setEditandoMarcaId(m.id)
    setEditandoMarcaNombre(m.nombre)
    setMensajeMarca(null)
  }

  const handleGuardarMarca = async () => {
    if (!editandoMarcaNombre.trim()) return
    setMensajeMarca(null)
    try {
      const res = await authFetch(`${API}/marcas/${editandoMarcaId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nombre: editandoMarcaNombre.trim() }),
      })
      if (!res.ok) {
        const err = await res.json()
        setMensajeMarca({ tipo: "error", texto: err.error || "Error al guardar" })
        return
      }
      setEditandoMarcaId(null)
      setEditandoMarcaNombre("")
      cargarMarcas()
      cargarProductos()
    } catch {
      setMensajeMarca({ tipo: "error", texto: "Error de conexión" })
    }
  }

  const handleEliminarMarca = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar la marca "${nombre}"? Los productos que la tenían asignada quedarán sin marca.`)) return
    await authFetch(`${API}/marcas/${id}`, { method: "DELETE" })
    cargarMarcas()
    cargarProductos()
  }

  const MarcaSelect = ({ name, value, onChange }) => (
    <select name={name} value={value} onChange={onChange}>
      <option value="">seleccionar marca...</option>
      {marcas.map(m => <option key={m.id} value={m.nombre}>{m.nombre}</option>)}
    </select>
  )

  return (
    <div className="adminPanel column">

      <div className="adminPanelTitle row">
        <i className="fa-solid fa-box"></i>
        CATÁLOGO DE PRODUCTOS
      </div>

      {/* ── Nuevo producto ─────────────────────────────────────── */}
      <div className="adminSection">
        <div className="adminSectionLabel">nuevo producto</div>
        <div className="adminRow">
          <div className="adminField">
            <label>marca</label>
            <MarcaSelect name="marcaNombre" value={form.marcaNombre} onChange={handleChange} />
          </div>
          <div className="adminField">
            <label>código</label>
            <input type="text" name="codigo" value={form.codigo} onChange={handleChange} placeholder="ej: REN-001" />
          </div>
          <div className="adminField">
            <label>descripción</label>
            <input type="text" name="descripcion" value={form.descripcion} onChange={handleChange} placeholder="ej: Parche 100mg" />
          </div>
          <div className="adminField">
            <label>unidades por caja</label>
            <input type="text" name="unidad" value={form.unidad} onChange={handleChange} placeholder="ej. 30" />
          </div>
        </div>
        {mensaje && (
          <div className={`adminMensaje adminMensaje--${mensaje.tipo}`}>
            {mensaje.texto}
          </div>
        )}
        <div className="adminBtnRow adminBtnRow--left">
          <button className="adminBtn adminBtn--primary" onClick={handleCrear}>
            agregar al catálogo
          </button>
        </div>
      </div>

      {/* ── Buscador ───────────────────────────────────────────── */}
      <div className="adminBusquedaRow">
        <i className="fa-solid fa-magnifying-glass"></i>
        <input
          type="text"
          value={busqueda}
          onChange={handleBusqueda}
          placeholder="buscar por código, descripción o marca..."
        />
        {busqueda && (
          <span className="adminBusquedaInfo">
            {productosFiltrados.length} resultado{productosFiltrados.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* ── Tabla de productos ─────────────────────────────────── */}
      <div className="adminPreviewBox">
        {productosFiltrados.length === 0 ? (
          <div className="adminEmptyState">
            {busqueda ? "no se encontraron productos" : "no hay productos en el catálogo"}
          </div>
        ) : (
          <table className="adminPreviewTable">
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th>marca</th>
                <th>código</th>
                <th>descripción</th>
                <th>unidad</th>
                <th style={{ width: 32 }}></th>
              </tr>
            </thead>
            <tbody>
              {productosPagina.map(p => (
                <>
                  <tr key={p.id} className={editandoId === p.id ? "usuarioRowActiva" : ""}>
                    <td>
                      <button
                        className={`usuarioEditBtn${editandoId === p.id ? " usuarioEditBtn--active" : ""}`}
                        onClick={() => handleEditar(p)}
                        title="editar"
                      >
                        <i className={`fa-solid fa-${editandoId === p.id ? "xmark" : "pen"}`}></i>
                      </button>
                    </td>
                    <td>{p.marca}</td>
                    <td>{p.codigo}</td>
                    <td>{p.descripcion}</td>
                    <td>{p.unidad}</td>
                    <td>
                      <button
                        className="usuarioEditBtn adminDanger"
                        onClick={() => handleBaja(p.id)}
                        title="dar de baja"
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </td>
                  </tr>

                  {editandoId === p.id && editForm && (
                    <tr key={`${p.id}-edit`} className="usuarioFormRow">
                      <td colSpan={6}>
                        <div className="usuarioFormInline">
                          <div className="adminSection">
                            <div className="adminRow">
                              <div className="adminField">
                                <label>marca</label>
                                <MarcaSelect name="marcaNombre" value={editForm.marcaNombre} onChange={handleEditChange} />
                              </div>
                              <div className="adminField">
                                <label>código</label>
                                <input type="text" name="codigo" value={editForm.codigo} onChange={handleEditChange} />
                              </div>
                              <div className="adminField">
                                <label>descripción</label>
                                <input type="text" name="descripcion" value={editForm.descripcion} onChange={handleEditChange} />
                              </div>
                              <div className="adminField">
                                <label>unidades por caja</label>
                                <input type="text" name="unidad" value={editForm.unidad} onChange={handleEditChange} />
                              </div>
                            </div>
                          </div>
                          <div className="adminBtnRow">
                            <button className="adminBtn adminBtn--primary" onClick={handleGuardar}>guardar</button>
                            <button className="adminBtn" onClick={() => { setEditandoId(null); setEditForm(null) }}>cancelar</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Paginación ─────────────────────────────────────────── */}
      {totalPaginas > 1 && (
        <div className="adminPaginacion">
          <button className="adminBtn" onClick={() => { setPagina(p => p - 1); setEditandoId(null) }} disabled={paginaReal === 1}>{"<"}</button>
          <span className="adminPaginacionInfo">{paginaReal} / {totalPaginas}</span>
          <button className="adminBtn" onClick={() => { setPagina(p => p + 1); setEditandoId(null) }} disabled={paginaReal === totalPaginas}>{">"}</button>
        </div>
      )}

      {/* ── Gestión de marcas ──────────────────────────────────── */}
      <div className="adminSection adminSection--mt">
        <div className="adminSectionLabel">gestión de marcas</div>
        <div className="adminRow">
          <div className="adminField">
            <label>nueva marca</label>
            <input
              type="text"
              value={nuevaMarca}
              onChange={e => setNuevaMarca(e.target.value)}
              placeholder="nombre de la marca"
              onKeyDown={e => e.key === "Enter" && handleAgregarMarca()}
            />
          </div>
          <button className="adminBtn adminBtn--primary adminBtnEnd" onClick={handleAgregarMarca}>
            + agregar
          </button>
        </div>
        {mensajeMarca && (
          <div className={`adminMensaje adminMensaje--${mensajeMarca.tipo}`}>
            {mensajeMarca.texto}
          </div>
        )}
        {marcas.length > 0 && (
          <div className="marcaChips">
            {marcas.map(m => (
              <div key={m.id} className="marcaChip">
                {editandoMarcaId === m.id ? (
                  <>
                    <input
                      className="marcaChipInput"
                      value={editandoMarcaNombre}
                      onChange={e => setEditandoMarcaNombre(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") handleGuardarMarca(); if (e.key === "Escape") { setEditandoMarcaId(null) } }}
                      autoFocus
                    />
                    <button className="marcaChipBtn" onClick={handleGuardarMarca} title="guardar">✓</button>
                    <button className="marcaChipBtn" onClick={() => setEditandoMarcaId(null)} title="cancelar">✕</button>
                  </>
                ) : (
                  <>
                    <span>{m.nombre}</span>
                    <button className="marcaChipBtn" onClick={() => handleEditarMarca(m)} title="editar marca">
                      <i className="fa-solid fa-pen"></i>
                    </button>
                    <button className="marcaChipBtn adminDanger" onClick={() => handleEliminarMarca(m.id, m.nombre)} title="eliminar marca">✕</button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  )
}

export default GestionProductos
